// @givmo/app-charity — `/org/team-access`: who is asking, who can act for the organization, who is
// on their way in, and what non-human things do (CPA-WEB40 · shell contract v3).
//
// ══ FOUR SECTIONS OF ONE SHARED PACKAGE, IN READING ORDER ══════════════════════════════════════
//
// The order is ONE rule across both consoles and every Team page (D-CP-51's amendment, Jonathan at
// the 2026-09-04 walkthrough: "Members should be the top-most section, then Invitations, then Access
// requests"): the people already on the team, then the people on their way in, then the people
// asking — and anything else the page carries after that.
//
//   1. THE ROSTER — the package's panel, the `org-console-members-*` family, the access editor
//      inside it. The per-module narrowing is unchanged: the Apps page's "who can open it" arrives
//      with `?module=<slug>`, and the roster filters the rows it has already loaded.
//   2. INVITES — the org-wide view of the ONE invite surface: the offers plus the composer that
//      makes one (each mounted module's Team page carries the same surface narrowed to its app).
//      This is the charity's ONLY org-wide invite surface: the legacy `/admins` page (its own
//      `/charity-admin` wire) retired with CPA-WEB41 — that address is an unknown path, never a
//      redirect.
//   3. ACCESS REQUESTS — the org-wide view of the ONE request table (T5.5, every app), a section of
//      this page rather than a page of its own (OQ4). Approve grants the level through the same
//      writer the editor uses; Deny takes an optional note the requester sees. The sidebar row
//      carries the count as a badge, and the panel its own pending badge, wherever it sits.
//   4. CONNECTED AI — the AI connections that act for the organization. Mounted on the company
//      console's Team access page too since OM-15, when the backend began binding an organization
//      connector to a company as well as to a charity.
//
// ⚠ THE GATE IS THE PAGE'S OWN CAPABILITY, `write_member_access` (wrapped at the route). The
// requests panel gates itself on the same capability and renders the calm statement for anyone
// else; the roster's actions are server-gated.
//
// DATA CONTRACT: `page-org-team-access` is the route's root; inside it the package's
// `org-access-requests-*` (fixed, not prefixed), `org-console-members-*`, `org-console-invites-*`
// and `org-console-connected-ai-*` families.

import { OrgTeamSections, TEAM_COPY } from '@givmo/org-console';
import { testIdProp } from '@givmo/design-system';
import { useNavigate } from '@tanstack/react-router';
import { PageShell } from '../../components/page-shell';
import { TEAM_ACCESS_ROUTE } from '../../console/console-routes';
import { useOrgConsoleConfig } from '../org-console/use-org-console-config';

const PREFIX = 'team-access';

export interface TeamAccessPageProps {
  /** The module the roster is narrowed to, from `?module=`. Absent ⇒ everyone. */
  module?: string;
}

export function TeamAccessPage({ module }: TeamAccessPageProps) {
  const config = useOrgConsoleConfig();
  const navigate = useNavigate();

  return (
    <PageShell testId="page-org-team-access" title={TEAM_COPY.adminTeam}>
      {config === null ? (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${PREFIX}-pending`)}>
          Loading…
        </p>
      ) : (
        <OrgTeamSections
          config={config}
          moduleFilter={module ?? null}
          onClearModuleFilter={() => void navigate({ to: TEAM_ACCESS_ROUTE, search: {} })}
        />
      )}
    </PageShell>
  );
}
